import { FC, useContext, useEffect, useState } from 'react';
import { AppFooter } from 'templates/AppFooter';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { Form } from 'templates/Form';
import { Button } from 'atoms/Button';
import { isUnderThirteen } from 'domains/User';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { useAuth } from 'hooks/useAuth';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { CreateUserDetailFormSchemaType } from 'commons/auditionForm';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateUserDetail } from 'hooks/u002/userUpdateUserDetailHooks';
import { useCreateUserDetailSchema } from 'hooks/u002/useCreateUserDetailSchema';
import { LabeledInputField } from 'molecules/LabeledInputField';
import { AppContext } from 'templates/AppContextProvider';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { DateOnlyPicker } from 'atoms/InputField';
import { isTaiwanPhone } from 'domains/PhoneNumber';
import { RequiredLabel } from 'molecules/RequiredLabel';
import { Country, getCountryCallingCode } from 'react-phone-number-input';

//U002F09 応募者情報詳細登録画面
type AuditionFormProps = {
  title: string;
  audition_id: string;
  examination_no: number;
  birth: Date;
  phoneNumber: string;
  now_dtm: Date;
  onNext: () => void;
};
export const UserDetailCreate: FC<AuditionFormProps> = (props) => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const [updateUserDetail, isLoadingUpdateUserDetail] = useUpdateUserDetail();
  const [isLoading, setIsLoading] = useState(false);
  const { isGlobal, currentLang } = useContext(AppContext);
  // 選択されたユーザーが13歳未満かつ国内ユーザであるか
  const isMinor = !isGlobal && props.birth != null && isUnderThirteen(props.birth, props.now_dtm);

  const CreateUserDetailFormSchema = useCreateUserDetailSchema(
    isMinor,
    props.phoneNumber,
    currentLang
  );
  const createUserFormDefaultValues: CreateUserDetailFormSchemaType = {
    firstName: '',
    lastName: '',
    firstNameKana: '',
    lastNameKana: '',
    mailAddress: '',
    birthday: new Date(props.birth.getFullYear(), props.birth.getMonth(), 1),
    parentName: '',
    parentPhoneNumber: '',
  };
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    clearErrors,
  } = useForm<CreateUserDetailFormSchemaType>({
    resolver: zodResolver(CreateUserDetailFormSchema),
    shouldFocusError: true,
    defaultValues: createUserFormDefaultValues,
  });
  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  const closeModal = () => {
    setShowModal(false);
  };

  console.log({ errors });
  const { username } = useAuth();
  const onSubmit: SubmitHandler<CreateUserDetailFormSchemaType> = async (form) => {
    console.log({ form });
    setIsLoading(true);

    await updateUserDetail({
      request: {
        username,
        firstName: form.firstName,
        lastName: form.lastName,
        firstNameKana: form.firstNameKana,
        lastNameKana: form.lastNameKana,
        birthday: new Date(form.birthday),
        mailAddress: form.mailAddress ?? '',
        parentName: form.parentName,
        parentPhoneNumber: form.parentPhoneNumber,
        auditionId: props.audition_id,
      },
      onSuccess: () => {
        setIsLoading(false);
        props.onNext();
      },
      onError: (error) => {
        console.log({ error });
        if (error instanceof Error) {
          setModalMessage(
            error.message === 'MM002F0093E'
              ? tAuditionForm('TU002_049')
              : error.message === 'MM002F0108E'
                ? tAuditionForm('TU002_060')
                : tAuditionForm('TU002_050')
          );
          setShowModal(true);
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <>
      {(isLoading || isLoadingUpdateUserDetail) && <LoadingModal />}
      {showModal && (
        <MessageModal
          message={modalMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={closeModal}
        />
      )}
      <AppLogoHeader />
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_009')}>
            {isTaiwanPhone(props.phoneNumber) ? (
              <LabeledInputField
                title={tAuditionForm('TU002_010')}
                ariaLabel={tAuditionForm('TU002_010')}
                placeholder={tAuditionForm('TU002_011')}
                {...register('mailAddress')}
                className="w-[25rem]"
                autoComplete="email"
                id="mailAddress"
                required={true}
              />
            ) : (
              <LabeledInputField
                title={tAuditionForm('TU002_010')}
                ariaLabel={tAuditionForm('TU002_010')}
                placeholder={tAuditionForm('TU002_011')}
                {...register('mailAddress')}
                className="w-[25rem]"
                autoComplete="email"
                id="mailAddress"
              />
            )}
            <FieldErrorMessages messages={[errors.mailAddress?.message]} />
            {currentLang === 'ja' && (
              <>
                <div className="flex flex-col gap-4">
                  <LabeledInputField
                    title={tAuditionForm('TU002_053')}
                    placeholder={tAuditionForm('TU002_021')}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_053')}
                    {...register('lastName')}
                    autoComplete="family-name"
                    id="lastName"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.lastName?.message]} />
                  <LabeledInputField
                    title={tAuditionForm('TU002_054')}
                    placeholder={tAuditionForm('TU002_022')}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_054')}
                    {...register('firstName')}
                    autoComplete="given-name"
                    id="firstName"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.firstName?.message]} />
                </div>
                <div className="flex flex-col gap-4">
                  <LabeledInputField
                    title={tAuditionForm('TU002_055')}
                    placeholder={'タナカ'}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_055')}
                    {...register('lastNameKana')}
                    autoComplete="family-name"
                    id="lastNameKana"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.lastNameKana?.message]} />
                  <LabeledInputField
                    title={tAuditionForm('TU002_056')}
                    placeholder={'タロウ'}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_056')}
                    {...register('firstNameKana')}
                    autoComplete="given-name"
                    id="firstNameKana"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.firstNameKana?.message]} />
                </div>
              </>
            )}
            {currentLang === 'en' && (
              <>
                <div className="flex flex-col gap-4">
                  <LabeledInputField
                    title={tAuditionForm('TU002_054')}
                    placeholder={tAuditionForm('TU002_022')}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_054')}
                    {...register('firstName')}
                    autoComplete="given-name"
                    id="firstName"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.firstName?.message]} />
                  <LabeledInputField
                    title={tAuditionForm('TU002_053')}
                    placeholder={tAuditionForm('TU002_021')}
                    className="w-[25rem]"
                    ariaLabel={tAuditionForm('TU002_053')}
                    {...register('lastName')}
                    autoComplete="family-name"
                    id="lastName"
                    required={true}
                  />
                  <FieldErrorMessages messages={[errors.lastName?.message]} />
                </div>
              </>
            )}
            <Controller
              control={control}
              name="birthday"
              render={({ field }) => (
                <div className="flex mb-2 py-2 flex-col">
                  <FormSectionTitle text={tAuditionForm('TU002_026')} htmlFor="birthday" />
                  <DateOnlyPicker
                    value={field.value}
                    onChange={(d) => {
                      field.onChange(d);
                    }}
                    id="birthday"
                  />
                </div>
              )}
            />
            <FieldErrorMessages messages={[errors.birthday?.message]} />
            {isMinor && (
              <>
                <div>
                  <div className="flex flex-col w-full mx-auto">
                    <LabeledInputField
                      title={tAuditionForm('TU002_097')}
                      placeholder={tAuditionForm('TU002_021') + ' ' + tAuditionForm('TU002_022')}
                      className="w-[25rem] mt-4"
                      ariaLabel={tAuditionForm('TU002_099')}
                      {...register('parentName')}
                      autoComplete="name"
                      id="parentName"
                      required={true}
                    />
                    <FieldErrorMessages messages={[errors.parentName?.message]} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <FormSectionTitle
                      text={tAuditionForm('TU002_113')}
                      htmlFor={'parentPhoneNumber'}
                    />
                    <RequiredLabel id="parentPhoneNumber" />
                  </div>
                  <div className="flex justify-start w-full max-w-[18rem] mt-2">
                    <Controller
                      name="parentPhoneNumber"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="parentPhoneNumber"
                          type="tel"
                          inputMode="numeric"
                          className="border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px] w-full"
                          placeholder="08012345678"
                          value={(field.value || '').replace(
                            new RegExp(`^\\+${getCountryCallingCode('JP' as Country)}`),
                            ''
                          )}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const countryCallingCode = getCountryCallingCode('JP' as Country);
                            const formattedValue = inputValue
                              ? `+${countryCallingCode}${inputValue}`
                              : '';
                            field.onChange(formattedValue);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </>
            )}
            <FieldErrorMessages messages={[errors.parentPhoneNumber?.message]} />
            <div className={'h-[20px]'} />
            <div className={'flex justify-center items-center'}>
              <Button className="w-[10rem]" label={tAuditionForm('TU002_058')} type="submit" />
            </div>
          </Form>
        </form>
      </PageContent>
      <AppFooter />
    </>
  );
};
